// lagform.ts — the in-game MP "LAG ALERT" Yes/No query (game/SimUI.cpp:1083-1171,
// OnLagNotify + CheckLagForm). The C++ shows a MODELESS DialogForm (kidfMessageBoxQuery)
// with kfFrmDoModal set BY HAND — no nested DoModal loop — and polls it each timer tick:
// while kfFrmDoModal is set the form is still up; when a button press EndForm's it (clearing
// the flag) CheckLagForm reads GetResult() (kidcOk = disconnect / kidcCancel = keep) and
// sends the CsKillLaggingPlayer vote.
//
// This module abstracts that modeless-poll surface behind LagFormUi so the SAME poll loop in
// gamesession.ts drives a DOM MessageBoxView. The DOM box rides a DEDICATED seam
// (env.domLagBox) with its OWN overlay instance — NOT the shared singleton env.domMessageBox
// seam — because the lag form is MODELESS and independent: it can be up WHILE a resign/observe
// box shows, and is hidden/re-shown for a different lagger out from under the user. The DOM box
// is intrinsically callback-shaped, so the adapter turns the user's done(fOk) into the polled
// (result, no-longer-modal) the loop expects: until the click, IsModal() stays true and
// GetResult() is unset; on click it captures the kidc result and IsModal() flips false —
// byte-identical vote, no Message/cmdq change.
//
// The canvas DialogForm path (CanvasLagForm over env.formsIni) was removed (canvas-removal R1).

import { kidcOk, kidcCancel } from './uikit.ts';
import type { MsgBoxEnv } from './msgbox.ts';

/** The dedicated lag-box seam: mount an independent (modeless) Yes/No overlay and return
 *  its teardown closure; done(fOk) fires on the user's Yes/No (the view tears itself down). */
export type DomLagBox = (title: string, body: string, done: (fOk: boolean) => void) => () => void;

/** The slice of the modeless lag form gamesession.ts's OnLagNotify/CheckLagForm touch.
 *  Both the canvas DialogForm and the DOM adapter satisfied it. */
export interface LagFormUi {
  /** show (true) / hide (false) the box. */
  Show(f: boolean): void;
  /** tear the box down (the C++ `delete pfrm`). */
  Dispose(): void;
  /** arm the modeless poll (the C++ `SetFlags(GetFlags() | kfFrmDoModal)`). */
  SetModal(): void;
  /** true while the box is still up awaiting the user (kfFrmDoModal still set). */
  IsModal(): boolean;
  /** kidcOk (disconnect) / kidcCancel (keep) once the user has chosen. */
  GetResult(): number;
}

/** Drive the DOM lag overlay (env.domLagBox) as a polled modeless box: the overlay is itself
 *  modal, so we hold IsModal() true until the user's done(fOk) lands, then capture the kidc
 *  result the poll reads. Show(false)/Dispose tear an UNANSWERED box down (lag cleared /
 *  different lagger) via the returned teardown closure. */
class DomLagForm implements LagFormUi {
  private modal = false;
  private result = kidcCancel;
  private teardown: (() => void) | null = null;
  private shown = false;
  private readonly mount: DomLagBox;
  private readonly title: string;
  private readonly body: string;

  constructor(mount: DomLagBox, title: string, body: string) {
    this.mount = mount;
    this.title = title;
    this.body = body;
  }

  SetModal(): void {
    this.modal = true;
  }
  IsModal(): boolean {
    return this.modal;
  }
  GetResult(): number {
    return this.result;
  }

  Show(f: boolean): void {
    if (f) {
      if (this.shown) return;
      this.shown = true;
      this.teardown = this.mount(this.title, this.body, (fOk) => {
        this.teardown = null; // the view tore itself down on the click
        this.result = fOk ? kidcOk : kidcCancel;
        this.modal = false; // CheckLagForm's poll now fires the vote
      });
    } else {
      this.shown = false;
      this.teardown?.();
      this.teardown = null;
    }
  }

  Dispose(): void {
    this.Show(false);
  }
}

/** OnLagNotify's lag box: a DOM box when env.domLagBox is set. Returns null when no
 *  DOM lag-box seam is wired (e.g. a headless harness). */
export function CreateLagForm(
  env: MsgBoxEnv & { domLagBox?: DomLagBox },
  title: string,
  body: string,
): LagFormUi | null {
  if (env.domLagBox !== undefined) {
    return new DomLagForm(env.domLagBox, title, body);
  }
  return null;
}
